import React, { ReactNode, useRef } from 'react'
import styles from './Tabs.module.sass'
import clsx from 'clsx'
import ChevronLeftIcon from 'files/chevron/left.svg'
import ChevronRightIcon from 'files/chevron/right.svg'
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import Button from '../../controls/Button/Button'
import { useOverflow } from '../../../utils/hooks'
import { clamp, ordNumber } from 'fp-ts/Ord'

interface Props<Item> {
    className?: string,
    items: Item[],
    selected?: Item,
    itemsKeys?: any,// eslint-disable-line
    renderer: (item: Item) => ReactNode,
    itemToKey: (item: Item) => string | number,
    onChange?: (item: Item) => void,
}

const clampNumber = clamp(ordNumber)

function Tabs<Item>(props: Props<Item>): JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const overflow = useOverflow(containerRef)

    const getDiff = () => (containerRef.current?.clientWidth ?? 0) - (contentRef.current?.scrollWidth ?? 0) - 40

    const xValue = useSpring(useMotionValue(0))
    const x = useTransform(xValue, clampNumber(getDiff(), 0))

    return <div ref={containerRef} className={clsx(props.className, styles.tabs)}>
        {
            overflow && <Button
                className={clsx(styles.nav, styles.backward)}
                onClick={() => xValue.set(x.get() + 200)}
            >
                <ChevronLeftIcon/>
            </Button>
        }
        <motion.div
            ref={contentRef}
            className={styles.content}
            drag={overflow? 'x': undefined}
            dragConstraints={{
                left: clampNumber(-Infinity, 0)(getDiff()),
                right: 0,
            }}
            style={{x}}
        >
            {
                props.items.map(item => <div
                    onDragStart={event => event.preventDefault()}
                    key={props.itemToKey(item)}
                    className={clsx(
                        styles.item,
                        {[styles.active]: props.selected && (props.itemToKey(item) === props.itemToKey(props.selected))},
                        {[styles.separator]: props.itemsKeys && props.itemsKeys.indexOf(props.itemToKey(item)) !== -1 }
                    )}
                    onClick={() => props.selected &&
                        props.itemToKey(props.selected) !== props.itemToKey(item) && props.onChange?.(item)
                    }
                >
                    {props.renderer(item)}
                </div>)
            }
        </motion.div>
        {
            overflow && <Button
                className={clsx(styles.nav, styles.forward)}
                onClick={() => xValue.set(x.get() - 200)}
            >
                <ChevronRightIcon/>
            </Button>
        }
    </div>
}


export default Tabs